
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface TacticalGearItem {
  name: string;
  price: number;
  category: string;
  weight: string;
  compatibility: string;
}

interface AccessoryItem {
  name: string;
  price: number;
  category: string;
  material: string;
  compatibility: string;
}

interface CartItem {
  name: string;
  price: number;
  quantity: number;
}

const TacticalGearPage = () => {
  const [activeTab, setActiveTab] = useState<'gear' | 'accessories'>('gear');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [discountPercent, setDiscountPercent] = useState<number>(0);

  const tacticalGear: TacticalGearItem[] = [
    { name: "Plate Carrier Vest", price: 250, category: "Protection", weight: "2.5 lbs", compatibility: "SAPI/ESAPI Plates" },
    { name: "Tactical Helmet", price: 180, category: "Protection", weight: "3.2 lbs", compatibility: "NVG Mount, Rails" },
    { name: "Combat Boots", price: 120, category: "Protection", weight: "2.8 lbs", compatibility: "All Terrain" },
    { name: "Tactical Gloves", price: 45, category: "Protection", weight: "0.3 lbs", compatibility: "Touchscreen Compatible" },
    { name: "Knee Pads", price: 35, category: "Protection", weight: "0.8 lbs", compatibility: "Universal Fit" },
    { name: "ACOG 4x32 Scope", price: 1200, category: "Optics", weight: "9.9 oz", compatibility: "Picatinny Rail" },
    { name: "Red Dot Sight", price: 350, category: "Optics", weight: "4.2 oz", compatibility: "Picatinny/Weaver" },
    { name: "Night Vision Goggles", price: 2500, category: "Optics", weight: "1.5 lbs", compatibility: "Helmet Mount" },
    { name: "Laser Rangefinder", price: 450, category: "Optics", weight: "7.5 oz", compatibility: "Handheld" },
    { name: "Tactical Flashlight", price: 85, category: "Optics", weight: "5.2 oz", compatibility: "Picatinny Mount" },
    { name: "Chest Rig", price: 95, category: "Loadcarriage", weight: "1.2 lbs", compatibility: "MOLLE Compatible" },
    { name: "Tactical Backpack", price: 180, category: "Loadcarriage", weight: "3.5 lbs", compatibility: "Hydration Ready" },
    { name: "Magazine Pouches", price: 25, category: "Loadcarriage", weight: "0.4 lbs", compatibility: "AR/AK Mags" },
    { name: "Utility Belt", price: 65, category: "Loadcarriage", weight: "0.9 lbs", compatibility: "MOLLE Attachments" },
    { name: "Drop Leg Holster", price: 75, category: "Loadcarriage", weight: "1.1 lbs", compatibility: "Most Pistols" }
  ];

  const accessories: AccessoryItem[] = [
    { name: "Tactical Sling", price: 35, category: "Weapon Accessories", material: "Nylon", compatibility: "Universal QD" },
    { name: "Bipod", price: 120, category: "Weapon Accessories", material: "Aluminum", compatibility: "Picatinny Rail" },
    { name: "Foregrip", price: 45, category: "Weapon Accessories", material: "Polymer", compatibility: "M-LOK/Picatinny" },
    { name: "Muzzle Brake", price: 85, category: "Weapon Accessories", material: "Steel", compatibility: "5.56/.223" },
    { name: "Suppressor", price: 650, category: "Weapon Accessories", material: "Titanium", compatibility: "Multi-Caliber" },
    { name: "Tactical Radio", price: 220, category: "Communication", material: "Polymer", compatibility: "Military Freq" },
    { name: "Headset", price: 180, category: "Communication", material: "Carbon Fiber", compatibility: "Radio Compatible" },
    { name: "GPS Device", price: 350, category: "Navigation", material: "Ruggedized", compatibility: "Military Grid" },
    { name: "Compass", price: 25, category: "Navigation", material: "Metal", compatibility: "Tritium Illuminated" },
    { name: "Multi-Tool", price: 95, category: "Utility", material: "Stainless Steel", compatibility: "Universal" },
    { name: "Tactical Knife", price: 125, category: "Utility", material: "Carbon Steel", compatibility: "MOLLE Sheath" },
    { name: "Paracord", price: 15, category: "Utility", material: "Nylon", compatibility: "550 lb Test" }
  ];

  const addToCart = (name: string, price: number) => {
    setCart(prev => {
      const existing = prev.find(item => item.name === name);
      if (existing) {
        return prev.map(item =>
          item.name === name ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { name, price, quantity: 1 }];
    });
  };

  const removeFromCart = (name: string) => {
    setCart(prev => prev.filter(item => item.name !== name));
  };

  const updateQuantity = (name: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(name);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.name === name ? { ...item, quantity } : item
      )
    );
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountAmount = subtotal * (discountPercent / 100);
  const taxableAmount = subtotal - discountAmount;
  const tax = taxableAmount * 0.04;
  const total = taxableAmount + tax;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg" style={{ fontFamily: '"Pacifico", serif' }}>TA</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>Tactical Arms</h1>
                <p className="text-xs text-orange-400">Tactical Gear & Equipment Pricing Reference</p>
              </div>
            </Link>
            
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-300 hover:text-orange-400 transition-colors">Home</Link>
              <Link to="/official-discounts" className="text-gray-300 hover:text-orange-400 transition-colors">Official Discounts</Link>
              <Link to="/shop" className="text-gray-300 hover:text-orange-400 transition-colors">Shop</Link>
            </nav>
            
            <div className="text-gray-400 text-sm">All prices in USD</div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Price Calculator */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Price Calculator</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Cart */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Selected Items</h3>
              <div className="bg-gray-900/50 rounded-xl p-4 min-h-[200px] max-h-[300px] overflow-y-auto">
                {cart.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No items selected</p>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.name} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.name}</p>
                          <p className="text-gray-400 text-sm">${item.price.toLocaleString()}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.name, item.quantity - 1)}
                            className="w-8 h-8 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center justify-center transition-colors"
                          >
                            <i className="ri-subtract-line"></i>
                          </button>
                          <span className="w-12 text-center text-white font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.name, item.quantity + 1)}
                            className="w-8 h-8 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center justify-center transition-colors"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Totals */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label htmlFor="discount" className="text-white font-medium">Discount %:</label>
                <input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(Number(e.target.value))}
                  className="w-20 px-3 py-2 bg-gray-800 border border-orange-500/30 rounded-lg text-white text-right focus:outline-none focus:border-orange-500"
                />
              </div>
              
              <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal:</span>
                  <span>${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Discount ({discountPercent}%):</span>
                  <span>-${discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Sales Tax (4.00%):</span>
                  <span>${tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="border-t border-gray-700 pt-3">
                  <div className="flex justify-between text-orange-400 font-bold text-lg">
                    <span>Total:</span>
                    <span>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-800/50 rounded-xl p-1 flex">
            <button
              onClick={() => setActiveTab('gear')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === 'gear'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Tactical Gear
            </button>
            <button
              onClick={() => setActiveTab('accessories')}
              className={`px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === 'accessories'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Accessories
            </button>
          </div>
        </div>

        {/* Tactical Gear Table */}
        {activeTab === 'gear' && (
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Select</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Item</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Price</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Category</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Weight</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Compatibility</th>
                  </tr>
                </thead>
                <tbody>
                  {tacticalGear.map((gear) => (
                    <tr key={gear.name} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={cart.some(item => item.name === gear.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addToCart(gear.name, gear.price);
                            } else {
                              removeFromCart(gear.name);
                            }
                          }}
                          className="w-5 h-5 text-orange-500 bg-gray-800 border-orange-500 rounded focus:ring-orange-500 focus:ring-2"
                        />
                      </td>
                      <td className="py-4 px-4 text-white font-medium">{gear.name}</td>
                      <td className="py-4 px-4 text-gray-300">${gear.price.toLocaleString()}</td>
                      <td className="py-4 px-4 text-gray-300">{gear.category}</td>
                      <td className="py-4 px-4 text-gray-300">{gear.weight}</td>
                      <td className="py-4 px-4 text-gray-300 text-sm">{gear.compatibility}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Accessories Table */}
        {activeTab === 'accessories' && (
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Select</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Item</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Price</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Category</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Material</th>
                    <th className="text-left py-4 px-4 text-orange-400 font-semibold">Compatibility</th>
                  </tr>
                </thead>
                <tbody>
                  {accessories.map((accessory) => (
                    <tr key={accessory.name} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={cart.some(item => item.name === accessory.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addToCart(accessory.name, accessory.price);
                            } else {
                              removeFromCart(accessory.name);
                            }
                          }}
                          className="w-5 h-5 text-orange-500 bg-gray-800 border-orange-500 rounded focus:ring-orange-500 focus:ring-2"
                        />
                      </td>
                      <td className="py-4 px-4 text-white font-medium">{accessory.name}</td>
                      <td className="py-4 px-4 text-gray-300">${accessory.price.toLocaleString()}</td>
                      <td className="py-4 px-4 text-gray-300">{accessory.category}</td>
                      <td className="py-4 px-4 text-gray-300">{accessory.material}</td>
                      <td className="py-4 px-4 text-gray-300 text-sm">{accessory.compatibility}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-black/80 border-t border-orange-500/20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400">© 2024 Tactical Arms - NXT Cam Properties</p>
              <p className="text-gray-500 text-sm">Retail Reference Only</p>
            </div>
            <div className="flex items-center space-x-6">
              <a href="https://readdy.ai/?origin=logo" className="text-gray-500 hover:text-orange-400 transition-colors text-sm">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TacticalGearPage;
